(function () {
  const TERMINAL_CASE_STATUSES = new Set([
    'AI_COMPLETED',
    'AI_PARTIAL_FAILED',
    'AI_FAILED'
  ]);

  const sleep = (delayMs) => new Promise((resolve) => window.setTimeout(resolve, delayMs));

  const EMBROIDERY_GUIDE_LONG_EDGE = 1200;
  const EMBROIDERY_GUIDE_MARGIN = 60;
  const EMBROIDERY_FONT_FAMILIES = {
    'classic-serif': 'Georgia, Times New Roman, serif',
    'modern-sans': 'Helvetica Neue, Arial, sans-serif',
    'elegant-script': 'Brush Script MT, Segoe Script, cursive',
    'bold-display': 'Impact, Arial Black, sans-serif',
    handwritten: 'Comic Sans MS, Bradley Hand, cursive',
    playfair: 'Playfair Display, Georgia, serif'
  };
  const EMBROIDERY_THREAD_COLORS = {
    gold: '#b3842f', black: '#161210', white: '#fffaf2', navy: '#15345b',
    burgundy: '#7a1f2d', forest: '#1f5b3d', rose: '#eb7985', coral: '#eb5e55',
    plum: '#9f173e', lavender: '#7891dd', blue: '#1477b9', emerald: '#3d9a61'
  };
  const MAX_PHOTO_LAYERS = 6;
  const MAX_COMPOSITE_BYTES = 9 * 1024 * 1024;

  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const normalizeEmbroideryTransform = (value) => {
    const transform = value && typeof value === 'object' ? value : {};
    const numberOr = (candidate, fallback) => candidate !== null && candidate !== '' && Number.isFinite(Number(candidate)) ? Number(candidate) : fallback;
    return {
      x: clamp(numberOr(transform.x, 50), 0, 100),
      y: clamp(numberOr(transform.y, 24), 0, 100),
      scale: clamp(numberOr(transform.scale, 100), 50, 250),
      rotation: clamp(numberOr(transform.rotation, 0), -180, 180)
    };
  };

  const loadGuideImage = (source) => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Embroidery logo could not be rendered in the placement guide'));
    image.src = source;
  });

  const canvasToPlacementFile = (canvas) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) { reject(new Error('Embroidery placement guide could not be encoded')); return; }
      resolve(new File([blob], 'embroidery-placement-guide.png', { type: 'image/png' }));
    }, 'image/png');
  });
  const canvasToFile = (canvas, name) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(new File([blob], name, { type: 'image/png' })) : reject(new Error('Photo composition could not be encoded')), 'image/png');
  });
  const normalizeCrop = (value) => {
    const width = clamp(Number(value?.width) || 1, .02, 1); const height = clamp(Number(value?.height) || 1, .02, 1);
    return { x: clamp(Number(value?.x) || 0, 0, 1 - width), y: clamp(Number(value?.y) || 0, 0, 1 - height), width, height };
  };
  const surfaceAspect = (size) => {
    const values = (String(size || '').match(/\d+(?:\.\d+)?/g) || []).map(Number).filter(Number.isFinite);
    return values.length === 2 && values[0] > 0 && values[1] > 0 ? values[0] / values[1] : 4 / 3;
  };
  const surfaceDimensions = (size, longEdge) => {
    const aspect = surfaceAspect(size);
    return aspect >= 1
      ? { width: longEdge, height: Math.max(1, Math.round(longEdge / aspect)) }
      : { width: Math.max(1, Math.round(longEdge * aspect)), height: longEdge };
  };
  const createSurfaceComposite = async (surface, size, side, longEdge = 2400) => {
    const photos = Array.isArray(surface?.photoLayers) ? surface.photoLayers.slice(0, MAX_PHOTO_LAYERS) : [];
    if (!photos.length) return null;
    if (photos.some((photo) => !photo.file || !photo.url || photo.needsReselect)) throw new Error(`Re-upload every ${side} photo before generating`);
    const canvas = document.createElement('canvas');
    const dimensions = surfaceDimensions(size, longEdge);
    canvas.width = dimensions.width; canvas.height = dimensions.height;
    const context = canvas.getContext('2d'); if (!context) throw new Error('Photo composition canvas is unavailable'); context.clearRect(0, 0, canvas.width, canvas.height);
    for (const photo of photos) {
      const image = await loadGuideImage(photo.url); const layout = photo.layout || photo; const crop = normalizeCrop(photo.crop);
      const numberOr = (value, fallback) => Number.isFinite(Number(value)) ? Number(value) : fallback;
      const frameWidth = canvas.width * clamp(numberOr(layout.width, 45), 2, 300) / 100; const frameHeight = canvas.height * clamp(numberOr(layout.height, 45), 2, 300) / 100;
      const centerX = canvas.width * clamp(numberOr(layout.x, 50), -50, 150) / 100; const centerY = canvas.height * clamp(numberOr(layout.y, 50), -50, 150) / 100;
      context.save(); context.translate(centerX, centerY); context.rotate(clamp(Number(layout.rotation) || 0, -180, 180) * Math.PI / 180); context.beginPath(); context.rect(-frameWidth / 2, -frameHeight / 2, frameWidth, frameHeight); context.clip();
      context.drawImage(image, image.naturalWidth * crop.x, image.naturalHeight * crop.y, image.naturalWidth * crop.width, image.naturalHeight * crop.height, -frameWidth / 2, -frameHeight / 2, frameWidth, frameHeight); context.restore();
    }
    return canvasToFile(canvas, `${side}-photo-composite.png`);
  };

  const drawMonochromeLogo = (context, image, width, height, color) => {
    const mask = document.createElement('canvas');
    mask.width = Math.max(1, Math.round(width));
    mask.height = Math.max(1, Math.round(height));
    const maskContext = mask.getContext('2d');
    maskContext.drawImage(image, 0, 0, mask.width, mask.height);
    maskContext.globalCompositeOperation = 'source-in';
    maskContext.fillStyle = color;
    maskContext.fillRect(0, 0, mask.width, mask.height);
    context.drawImage(mask, -width / 2, -height / 2, width, height);
  };

  const createEmbroideryPlacementGuide = async (payload, state) => {
    const front = payload?.caseConfig?.surfaces?.front;
    const embroidery = front?.embroidery;
    if (!embroidery?.enabled || !['text', 'logo'].includes(embroidery.type)) return null;

    const canvas = document.createElement('canvas');
    const dimensions = surfaceDimensions(payload?.caseConfig?.size, EMBROIDERY_GUIDE_LONG_EDGE);
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Embroidery placement canvas is unavailable');

    const surfaceX = EMBROIDERY_GUIDE_MARGIN;
    const surfaceY = EMBROIDERY_GUIDE_MARGIN;
    const surfaceWidth = canvas.width - EMBROIDERY_GUIDE_MARGIN * 2;
    const surfaceHeight = canvas.height - EMBROIDERY_GUIDE_MARGIN * 2;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#f3efe9';
    context.fillRect(surfaceX, surfaceY, surfaceWidth, surfaceHeight);
    context.strokeStyle = '#8d8379';
    context.lineWidth = 4;
    context.strokeRect(surfaceX, surfaceY, surfaceWidth, surfaceHeight);

    const transform = normalizeEmbroideryTransform(embroidery.transform || front.textTransform);
    const centerX = surfaceX + surfaceWidth * transform.x / 100;
    const centerY = surfaceY + surfaceHeight * transform.y / 100;
    const threadColor = embroidery.threadColorHex || EMBROIDERY_THREAD_COLORS[embroidery.threadColorId] || '#7a1f2d';
    context.save();
    context.translate(centerX, centerY);
    context.rotate(transform.rotation * Math.PI / 180);

    if (embroidery.type === 'text') {
      const fontFamily = EMBROIDERY_FONT_FAMILIES[embroidery.fontId] || EMBROIDERY_FONT_FAMILIES['classic-serif'];
      context.fillStyle = threadColor;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.font = `600 ${Math.round(72 * transform.scale / 100)}px ${fontFamily}`;
      context.fillText(String(embroidery.text || ''), 0, 0, surfaceWidth * .82);
    } else {
      const logoSurface = state?.surfaces?.front || {};
      let logoSource = logoSurface.embroideryLogoUrl || logoSurface.embroideryLogoSourceUrl || '';
      let objectUrl = '';
      if (!logoSource && logoSurface.embroideryLogoFile) {
        objectUrl = URL.createObjectURL(logoSurface.embroideryLogoFile);
        logoSource = objectUrl;
      }
      if (!logoSource) throw new Error('Embroidery logo is unavailable for the placement guide');
      try {
        const image = await loadGuideImage(logoSource);
        const logoWidth = surfaceWidth * .22 * transform.scale / 100;
        const naturalRatio = image.naturalWidth > 0 && image.naturalHeight > 0 ? image.naturalHeight / image.naturalWidth : .75;
        const logoHeight = Math.min(surfaceHeight * .7, logoWidth * naturalRatio);
        drawMonochromeLogo(context, image, logoWidth, logoHeight, threadColor);
      } finally {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }
    }
    context.restore();
    return canvasToPlacementFile(canvas);
  };

  const serializeSurfaces = (state, studioSnapshot) => {
    const stateSurfaces = state.surfaces || {};
    const snapshotSurfaces = studioSnapshot.surfaces || {};
    const sides = studioSnapshot.productType === 'FLANNEL_DOUBLE_PRINT' ? ['front', 'back'] : ['front'];
    return sides.reduce((surfaces, side) => {
      const surface = Object.assign({}, snapshotSurfaces[side] || {}, stateSurfaces[side] || {});
      const sourceType = String(surface.sourceType || (surface.templateId ? 'TEMPLATE' : 'UPLOAD')).toUpperCase();
      const usesUpload = sourceType === 'UPLOAD';
      const photos = usesUpload && Array.isArray(surface.photoLayers) ? surface.photoLayers.slice(0, MAX_PHOTO_LAYERS) : [];
      const embroideryType = side === 'front' && ['text', 'logo'].includes(surface.embroideryType) ? surface.embroideryType : 'none';
      surfaces[side] = {
        sourceType,
        templateId: usesUpload ? undefined : surface.templateId || undefined,
        customText: undefined,
        hasUploadedImage: usesUpload ? Boolean(surface.uploadedFile || photos.length) : false,
        photoLayers: photos.length ? photos.map((photo, fileIndex) => ({
          id: photo.id, fileIndex, name: photo.name, zIndex: fileIndex,
          layout: photo.layout || { x: photo.x, y: photo.y, width: photo.width, height: photo.height, rotation: photo.rotation },
          crop: normalizeCrop(photo.crop), intrinsic: photo.intrinsic || { width: photo.intrinsicWidth, height: photo.intrinsicHeight }
        })) : undefined,
        textFont: side === 'front' ? surface.textFont || undefined : undefined,
        textColor: side === 'front' ? surface.textColor || undefined : undefined,
        textPosition: side === 'front' ? surface.textPosition || 'custom' : undefined,
        artworkTransform: surface.artworkTransform || undefined,
        textTransform: side === 'front' ? surface.textTransform || undefined : undefined,
        textStyle: side === 'front' ? surface.textStyle || undefined : undefined,
        embroidery: Object.assign({}, surface.embroidery || {}, {
          enabled: embroideryType !== 'none',
          type: embroideryType,
          text: embroideryType === 'text' ? surface.text || undefined : undefined,
          fontId: side === 'front' ? surface.textFont || undefined : undefined,
          threadColorId: side === 'front' ? surface.textColor || undefined : undefined,
          threadColorHex: side === 'front' ? EMBROIDERY_THREAD_COLORS[surface.textColor] || undefined : undefined,
          logoName: embroideryType === 'logo' ? surface.embroideryLogoName || undefined : undefined,
          transform: side === 'front' ? surface.textTransform || undefined : undefined
        })
      };
      return surfaces;
    }, {});
  };

  const createDesignPayload = (state, studioSnapshot) => {
    const front = state.surfaces?.front || studioSnapshot.surfaces?.front || {};
    const cardEnabled = !studioSnapshot.cardSkipped && Boolean(studioSnapshot.cardPresetId && studioSnapshot.cardMessage);
    const fixedPoptexBlanket = studioSnapshot.productMode === 'readyMade' && Boolean(studioSnapshot.fixedTemplateId);
    const serializedSurfaces = serializeSurfaces(state, studioSnapshot);
    if (fixedPoptexBlanket && serializedSurfaces.front) {
      serializedSurfaces.front.sourceType = 'FIXED';
      serializedSurfaces.front.templateId = studioSnapshot.fixedTemplateId;
      serializedSurfaces.front.hasUploadedImage = false;
      serializedSurfaces.front.photoLayers = undefined;
      serializedSurfaces.front.artworkTransform = undefined;
    }
    const usesEmbroideryLogo = serializedSurfaces.front?.embroidery?.type === 'logo';
    const usesTemplateArtwork = serializedSurfaces.front?.sourceType === 'TEMPLATE';
    const editorState = {
      version: 2,
      productMode: studioSnapshot.productMode,
      productType: studioSnapshot.productType,
      fixedTemplateId: studioSnapshot.fixedTemplateId || undefined,
      size: studioSnapshot.size,
      surfaces: Object.fromEntries(Object.entries(state.surfaces || {})
        .filter(([side]) => !fixedPoptexBlanket || side === 'front')
        .map(([side, surface]) => {
          const serialized = serializedSurfaces[side] || {};
          const usesUpload = serialized.sourceType === 'UPLOAD';
          return [side, {
            sourceType: fixedPoptexBlanket && side === 'front' ? 'fixed' : String(serialized.sourceType || surface.sourceType || '').toLowerCase(),
            templateId: fixedPoptexBlanket && side === 'front' ? studioSnapshot.fixedTemplateId : serialized.templateId,
            uploadName: !fixedPoptexBlanket && usesUpload ? surface.uploadName || undefined : undefined,
            artworkTransform: surface.artworkTransform,
            textTransform: side === 'front' ? surface.textTransform : undefined,
            textStyle: side === 'front' ? surface.textStyle : undefined,
            embroideryLogoTolerance: side === 'front' ? surface.embroideryLogoTolerance : undefined,
            photoLayers: fixedPoptexBlanket && side === 'front' ? undefined : serialized.photoLayers
          }];
        })),
      embroiderySkipped: Boolean(state.embroiderySkipped),
      logoName: state.logoName || undefined,
      logoCleanup: {
        tolerance: state.surfaces?.front?.embroideryLogoTolerance,
        backgroundMode: state.surfaces?.front?.embroideryLogoBackgroundMode || undefined
      },
      cardPresetId: studioSnapshot.cardPresetId || undefined,
      cardMessage: studioSnapshot.cardMessage || '',
      cardSkipped: Boolean(studioSnapshot.cardSkipped),
      brandPresetId: studioSnapshot.brandMarkPresetId || undefined,
      brandSkipped: Boolean(studioSnapshot.brandSkipped),
      brandMarkTransform: studioSnapshot.brandMarkTransform,
      packagingPresetId: studioSnapshot.packagingPresetId || undefined,
      quantity: studioSnapshot.commerce?.quantity,
      shipping: studioSnapshot.commerce?.shipping
    };
    return {
      anonymousId: studioSnapshot.anonymousId,
      sceneId: fixedPoptexBlanket ? studioSnapshot.fixedTemplate?.sceneId : usesTemplateArtwork ? front.template?.sceneId || studioSnapshot.sceneId || undefined : undefined,
      eventId: fixedPoptexBlanket ? studioSnapshot.fixedTemplate?.eventId : usesTemplateArtwork ? front.template?.eventId || studioSnapshot.eventId || undefined : undefined,
      templateId: fixedPoptexBlanket ? studioSnapshot.fixedTemplateId : usesTemplateArtwork ? serializedSurfaces.front.templateId : undefined,
      materialPresetId: fixedPoptexBlanket ? undefined : studioSnapshot.materialPresetId,
      generatePreview: true,
      customText: undefined,
      caseConfig: {
        productType: fixedPoptexBlanket ? 'POPTEX_BLANKET' : studioSnapshot.productType || 'FLANNEL_SINGLE_PRINT',
        size: studioSnapshot.size || '50 x 60',
        artworkMode: fixedPoptexBlanket ? 'poptex-blanket' : 'customizable',
        templateType: fixedPoptexBlanket ? 'POPTEX_BLANKET' : 'CUSTOMIZABLE',
        fixedTemplateId: fixedPoptexBlanket ? studioSnapshot.fixedTemplateId : undefined,
        shopifyProductHandle: fixedPoptexBlanket ? studioSnapshot.fixedTemplate?.shopifyProductHandle || undefined : undefined,
        previewMode: 'five-images',
        editorState,
        commerce: studioSnapshot.commerce || {},
        display: studioSnapshot.display || {},
        surfaces: serializedSurfaces
      },
      labelConfig: {
        presetId: studioSnapshot.brandMarkPresetId,
        style: studioSnapshot.brandMarkName || undefined,
        hasLogo: Boolean(state.logoFile) && (!studioSnapshot.brandSkipped || usesEmbroideryLogo),
        transform: studioSnapshot.brandMarkTransform,
        logoCleanup: editorState.logoCleanup
      },
      packageConfig: {
        presetId: studioSnapshot.packagingPresetId,
        style: studioSnapshot.packagingName || undefined,
        hasLogo: Boolean(state.logoFile),
        messageCard: {
          enabled: cardEnabled,
          presetId: cardEnabled ? studioSnapshot.cardPresetId : undefined,
          message: cardEnabled ? studioSnapshot.cardMessage : ''
        }
      },
      promptSnapshot: {
        source: fixedPoptexBlanket ? 'poptex-blanket' : Object.values(state.surfaces || {}).some((surface) => surface?.sourceType === 'upload') ? 'upload' : 'template',
        fixedProduct: fixedPoptexBlanket ? {
          templateId: studioSnapshot.fixedTemplateId,
          name: studioSnapshot.fixedTemplate?.name,
          materialName: studioSnapshot.fixedTemplate?.materialName,
          colorName: studioSnapshot.fixedTemplate?.colorName,
          preserveExactly: true
        } : undefined
      },
      aiParamsSnapshot: {
        requestedFrom: 'shopify-theme-design-studio'
      }
    };
  };

  const listScenes = (client) =>
    client && client.cms && client.cms.listScenes
      ? client.cms.listScenes()
      : Promise.resolve([]);
  const listSceneEvents = (client, sceneId) =>
    client && client.cms && client.cms.listSceneEvents && sceneId
      ? client.cms.listSceneEvents(sceneId)
      : Promise.resolve([]);
  const listEventTemplates = (client, eventId) =>
    client && client.cms && client.cms.listEventTemplates && eventId
      ? client.cms.listEventTemplates(eventId)
      : Promise.resolve([]);
  const listTemplates = (client, query) =>
    client && client.cms && client.cms.listTemplates
      ? client.cms.listTemplates(query)
      : Promise.resolve([]);
  const listReferencePresets = (client, query) =>
    client && client.cms && client.cms.listReferencePresets
      ? client.cms.listReferencePresets(query)
      : Promise.resolve([]);

  const createCaseBody = async (payload, state) => {
    const formData = new FormData();
    formData.append('payload', JSON.stringify(payload));
    const fixedPoptexBlanket = payload.caseConfig.artworkMode === 'poptex-blanket' || payload.caseConfig.productType === 'POPTEX_BLANKET';
    for (const side of ['front', 'back']) {
      if (!payload.caseConfig.surfaces[side] || payload.caseConfig.surfaces[side].sourceType !== 'UPLOAD') continue;
      if (fixedPoptexBlanket) continue;
      const surface = state.surfaces?.[side] || {}; const photos = Array.isArray(surface.photoLayers) ? surface.photoLayers.slice(0, MAX_PHOTO_LAYERS) : [];
      if (photos.length) {
        let composite;
        try {
          composite = await createSurfaceComposite(surface, payload.caseConfig.size, side, 2400);
          if (composite?.size > MAX_COMPOSITE_BYTES) composite = await createSurfaceComposite(surface, payload.caseConfig.size, side, 1800);
        }
        catch (error) { console.warn('High-resolution composition failed; retrying at 1800px.', error); composite = await createSurfaceComposite(surface, payload.caseConfig.size, side, 1800); }
        if (!composite) throw new Error(`${side} photo composition is missing`);
        if (composite.size > MAX_COMPOSITE_BYTES) throw new Error(`${side} photo composition is too large. Crop the photos or use smaller source files.`);
        formData.append(`${side}CompositeImage`, composite);
        photos.forEach((photo) => formData.append(`${side}UserImages`, photo.file));
      } else if (surface.uploadedFile) formData.append(`${side}UserImages`, surface.uploadedFile);
    }
    if (payload.labelConfig.hasLogo && state.logoFile) formData.append('logoImages', state.logoFile);
    try {
      const placementGuide = await createEmbroideryPlacementGuide(payload, state);
      if (placementGuide) formData.append('embroideryPlacementImages', placementGuide);
    } catch (error) {
      console.warn('Unable to create embroidery placement guide; coordinate instructions will still be used.', error);
    }

    return formData;
  };

  const pollCase = async (client, caseId, onProgress) => {
    const deadline = Date.now() + 8 * 60 * 1000;
    while (Date.now() < deadline) {
      const caseData = await client.cases.get(caseId);
      if (onProgress) onProgress(caseData);
      if (TERMINAL_CASE_STATUSES.has(caseData.status)) return caseData;
      await sleep(2500);
    }
    throw new Error('AI generation timed out. The case remains available for retry.');
  };

  const requestPreview = async (options) => {
    const client = options.client;
    const state = options.state;
    const studioSnapshot = options.studioSnapshot || {};
    if (!client) throw new Error('POPTEX API client is unavailable');
    const configuredSurfaces = state.surfaces && Object.values(state.surfaces);
    const hasSurfaceSource = configuredSurfaces && configuredSurfaces.some((surface) => surface && (surface.templateId || surface.uploadedFile || surface.photoLayers?.length));
    const fixedPoptexBlanket = studioSnapshot.productMode === 'readyMade' && Boolean(studioSnapshot.fixedTemplateId);
    if (!fixedPoptexBlanket && !hasSurfaceSource) {
      throw new Error('Choose a template or upload an image first');
    }
    const anonymousId = client.getAnonymousId();
    const payload = createDesignPayload(
      state,
      Object.assign({}, studioSnapshot, { anonymousId })
    );
    if ((!fixedPoptexBlanket && (!payload.materialPresetId || !payload.labelConfig.presetId)) || !payload.packageConfig.presetId) {
      throw new Error('Material, brand mark, and packaging presets are required');
    }
    const templateIds = Array.from(new Set(Object.values(payload.caseConfig.surfaces || {})
      .map((surface) => surface && surface.templateId)
      .filter(Boolean)));
    const selection = {
      sceneId: payload.sceneId,
      eventId: payload.eventId,
      templateId: payload.templateId,
      templateIds
    };
    if (options.onStage) options.onStage({ phase: 'validation', progress: 10, copy: 'Configuration validated. Preparing a secure upload.' });
    const uploadToken = await client.storefront.createCaseUploadToken(selection);
    if (options.onStage) options.onStage({ phase: 'upload', progress: 22, copy: 'Secure upload authorized. Sending your customization materials.' });
    const body = await createCaseBody(payload, state);
    const result = await client.cases.create(body, { uploadToken });
    const createdCase = result && result.case ? result.case : result;
    if (!createdCase || !createdCase.id) {
      throw new Error('Case creation response is missing a case ID');
    }

    state.caseId = createdCase.id;
    state.serverCase = createdCase;
    if (options.onStage) options.onStage({ phase: 'case', progress: 40, copy: 'Your Case was created. POPTEX AI is starting generation.' });
    if (options.onProgress) options.onProgress(createdCase);
    const caseData = await pollCase(client, createdCase.id, options.onProgress);
    state.serverCase = caseData;
    const images = Array.isArray(caseData.displayImages)
      ? caseData.displayImages.filter((image) => image && image.url)
      : [];
    if (caseData.status === 'AI_FAILED' || images.length === 0) {
      throw new Error(
        (caseData.currentGeneration && caseData.currentGeneration.errorMessage) ||
          'AI generation failed'
      );
    }
    return {
      imageUrl: images[0].url,
      images,
      partial: caseData.status === 'AI_PARTIAL_FAILED' || images.length < 5,
      source: 'api',
      raw: caseData,
      payload
    };
  };

  window.PoptexDesignStudioApi = {
    listScenes,
    listSceneEvents,
    listEventTemplates,
    listTemplates,
    listReferencePresets,
    requestPreview
  };
})();
